import { z } from 'zod';

// admin-analytics-expansion P4a (D9, spec ticket-trends #2559) — daily-bucketed
// volume + net-revenue time-series contract. Single source of truth shared by
// AdminAnalyticsController's `GET /admin/analytics/trends` (backend) and
// getTicketTrends() (api-client). Mirrors ticket-analytics-schemas.ts /
// ticket-browse-schemas.ts conventions (query schema + response schema in
// one file).
//
// NOTE: the `from`/`to`/`refine` shape below intentionally DUPLICATES
// AnalyticsRangeQuerySchema (packages/shared/src/ticket-analytics-schemas.ts)
// rather than `.extend`-ing it: `AnalyticsRangeQuerySchema` is a
// `ZodEffects` (the result of `.refine(...)`), and `ZodEffects` has no
// `.extend` method — the same duplication pattern is already used by
// TicketBrowseQuerySchema for the identical reason.

// fresh-review fix #4 (LOW/MED) — cap the maximum requestable span so an
// authenticated caller cannot force a huge table scan + an oversized per-day
// bucket array by requesting a multi-decade range.
const MAX_RANGE_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

export const TicketTrendsQuerySchema = z
  .object({
    locationId: z.string().uuid().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
  })
  .refine((v) => !v.from || !v.to || new Date(v.from) <= new Date(v.to), {
    message: "'to' must not precede 'from'",
    path: ['to'],
  })
  .refine(
    (v) =>
      !v.from ||
      !v.to ||
      new Date(v.to).getTime() - new Date(v.from).getTime() <= MAX_RANGE_DAYS * DAY_MS,
    { message: `Range must not exceed ${MAX_RANGE_DAYS} days`, path: ['to'] },
  );
export type TicketTrendsQuery = z.infer<typeof TicketTrendsQuerySchema>;

export const TicketTrendBucketSchema = z.object({
  /** Local-calendar-day key, 'YYYY-MM-DD'. Never omitted — empty days are zero-filled. */
  day: z.string(),
  volume: z.number().int().nonnegative(),
  // amountPaidCents - tipAmountCents (tips excluded, JQ4-14 convention).
  netRevenueCents: z.number().int(),
});
export type TicketTrendBucket = z.infer<typeof TicketTrendBucketSchema>;

export const TicketTrendsResponseSchema = z.object({
  from: z.string(), // ISO 8601 UTC
  to: z.string(), // ISO 8601 UTC
  /** IANA timezone used to bucket days — the requested location's own timezone, or 'UTC' for an org-wide rollup. */
  tz: z.string(),
  buckets: z.array(TicketTrendBucketSchema),
});
export type TicketTrendsResponse = z.infer<typeof TicketTrendsResponseSchema>;
